
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { IShortCommunity } from '@/model/Community/communityInterfaces';
import { collectErrors, errorMessage, fieldErrorMessages, generalErrorMessage } from '@/components/settings/identityIntegrations/integrationErrors';
import {
  CANONICAL_FIELD_COMMUNITY_REFERENCE,
  COMMUNITY_RESOLUTION_ATTRIBUTE_MAPPING,
  COMMUNITY_RESOLUTION_FIXED,
  IApiFieldError,
  identityIntegrationsService,
  IIdentityCommunityMapping,
  IIdentityIntegration,
} from '@/services/identityIntegrationsService';

const communityModule = namespace('communityModule');

const SEARCH_DEBOUNCE_MS = 350;

/**
 * Community resolution: a mode, plus — under attribute mapping — the external
 * value table.
 *
 * The external value is opaque, so this offers a plain text field and a
 * substring search over it. Nothing here interprets, validates or formats it:
 * a partner code, a UUID and something meaningless all have to work.
 */
@Component({ name: 'CommunityResolutionCard' })
export default class CommunityResolutionCard extends Vue {
  @Prop({ required: true })
  protected integration!: IIdentityIntegration;
  @Prop({ default: false })
  protected canManage!: boolean;

  @communityModule.Getter('communityList')
  private communities!: IShortCommunity[];

  @communityModule.Action('fetchAllCommunities')
  private fetchAllCommunities!: () => void;

  protected readonly modeFixed = COMMUNITY_RESOLUTION_FIXED;
  protected readonly modeAttributeMapping = COMMUNITY_RESOLUTION_ATTRIBUTE_MAPPING;

  protected mode: string = COMMUNITY_RESOLUTION_FIXED;
  protected mappings: IIdentityCommunityMapping[] = [];
  protected total: number = 0;
  protected search: string | null = null;
  protected options: any = { page: 1, itemsPerPage: 10 };
  protected loading: boolean = false;
  protected busy: boolean = false;

  protected editing: boolean = false;
  protected editorMapping: IIdentityCommunityMapping | null = null;
  protected editorExternalValue: string | null = null;
  protected editorCommunityId: number | null = null;
  protected editorErrors: IApiFieldError[] = [];

  protected removing: boolean = false;
  protected removingMapping: IIdentityCommunityMapping | null = null;

  protected modeErrors: IApiFieldError[] = [];
  protected listError: string | null = null;

  private searchTimer: any = null;

  created() {
    if (!this.communities || !this.communities.length) {
      this.fetchAllCommunities();
    }
    this.resetMode();
    this.reload();
  }

  @Watch('integration')
  onIntegrationChanged() {
    this.resetMode();
    this.reload();
  }

  @Watch('options', { deep: true })
  onOptionsChanged() {
    this.reload();
  }

  // Vuetify is not wired to the app's i18n, so its footer would stay in
  // English on a Spanish screen unless it is given the words.
  get footerProps() {
    return {
      'items-per-page-options': [10, 25, 50],
      'items-per-page-text': this.$t('common.paginator.rowsPerPage'),
      'page-text': '{0}-{1} ' + this.$t('common.paginator.of') + ' {2}',
    };
  }

  get headers() {
    return [
      { text: this.$t('identityIntegrations.communityResolution.externalValue'), value: 'external_value' },
      { text: this.$t('identityIntegrations.communityResolution.smartPointCommunity'), value: 'community_id' },
      { text: '', value: 'actions', sortable: false, align: 'end', width: 100 },
    ];
  }

  get modeDirty(): boolean {
    return this.mode !== this.integration.community_resolution.mode;
  }

  /**
   * Attribute-mapped resolution cannot work without knowing which attribute
   * carries the reference, so say so here rather than only at activation.
   */
  get showMissingReferenceWarning(): boolean {
    if (this.mode !== COMMUNITY_RESOLUTION_ATTRIBUTE_MAPPING) {
      return false;
    }

    const attributes = this.integration.identity_mapping.attributes || [];

    return !attributes.some((mapping) => mapping.canonical_field === CANONICAL_FIELD_COMMUNITY_REFERENCE);
  }

  get modeError(): string | null {
    return generalErrorMessage(this, this.modeErrors, []);
  }

  get scopedCommunityItems() {
    const communities = this.communities || [];
    const scope = this.integration.community_ids || [];

    // Only Communities already in the authentication scope: a mapping selects
    // AMONG what the Integration may authenticate, it never adds one. The
    // backend refuses the rest anyway; offering them would just invite a
    // rejected save.
    return communities
      .filter((community: IShortCommunity) => !community.deleted_at && scope.indexOf(community.id) !== -1)
      .map((community: IShortCommunity) => ({ text: community.name + ' (#' + community.id + ')', value: community.id }));
  }

  get editorValid(): boolean {
    return !!(this.editorExternalValue || '').trim() && !!this.editorCommunityId;
  }

  get editorGeneralError(): string | null {
    return generalErrorMessage(this, this.editorErrors, ['external_value', 'community_id']);
  }

  get removingExternalValue(): string {
    return this.removingMapping ? this.removingMapping.external_value : '';
  }

  communityLabel(communityId: number): string {
    const communities = this.communities || [];
    const found = communities.filter((community: IShortCommunity) => community.id === communityId);

    return found.length ? found[0].name + ' (#' + communityId + ')' : '#' + communityId;
  }

  editorFieldError(field: string): string[] {
    return fieldErrorMessages(this, this.editorErrors, field);
  }

  resetMode() {
    this.mode = this.integration.community_resolution.mode;
    this.modeErrors = [];
  }

  onSearch() {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }

    this.searchTimer = setTimeout(() => {
      this.options = Object.assign({}, this.options, { page: 1 });
      this.reload();
    }, SEARCH_DEBOUNCE_MS);
  }

  async reload() {
    if (this.integration.community_resolution.mode !== COMMUNITY_RESOLUTION_ATTRIBUTE_MAPPING) {
      this.mappings = [];
      this.total = 0;
      return;
    }

    this.loading = true;
    this.listError = null;
    try {
      const page = await identityIntegrationsService.communityMappings(
        this.integration.id,
        this.options.page || 1,
        this.options.itemsPerPage || 10,
        this.search
      );
      this.mappings = page.data;
      this.total = page.total;
    } catch (error) {
      this.listError = errorMessage(this, collectErrors(error)[0]);
    } finally {
      this.loading = false;
    }
  }

  async saveMode() {
    this.busy = true;
    this.modeErrors = [];
    try {
      const updated = await identityIntegrationsService.update(this.integration.id, {
        community_resolution_mode: this.mode,
      });
      this.$uiBus.emit('info', this.$t('identityIntegrations.saved') as string);
      this.$emit('updated', updated);
    } catch (error) {
      this.modeErrors = collectErrors(error);
    } finally {
      this.busy = false;
    }
  }

  openEditor(mapping: IIdentityCommunityMapping | null) {
    this.editorMapping = mapping;
    this.editorExternalValue = mapping ? mapping.external_value : null;
    this.editorCommunityId = mapping ? mapping.community_id : null;
    this.editorErrors = [];
    this.editing = true;
  }

  closeEditor() {
    this.editing = false;
    this.editorErrors = [];
  }

  async saveMapping() {
    this.busy = true;
    this.editorErrors = [];
    try {
      const updated = await identityIntegrationsService.saveCommunityMapping(
        this.integration.id,
        this.editorMapping ? this.editorMapping.id : null,
        (this.editorExternalValue || '').trim(),
        this.editorCommunityId as number
      );
      this.editing = false;
      this.$uiBus.emit('info', this.$t('identityIntegrations.saved') as string);
      this.$emit('updated', updated);
      this.reload();
    } catch (error) {
      this.editorErrors = collectErrors(error);
    } finally {
      this.busy = false;
    }
  }

  confirmRemove(mapping: IIdentityCommunityMapping) {
    this.removingMapping = mapping;
    this.removing = true;
  }

  async removeMapping() {
    if (!this.removingMapping) {
      return;
    }

    this.busy = true;
    try {
      const updated = await identityIntegrationsService.deleteCommunityMapping(
        this.integration.id,
        this.removingMapping.id
      );
      this.removing = false;
      this.$uiBus.emit('info', this.$t('identityIntegrations.saved') as string);
      this.$emit('updated', updated);
      this.reload();
    } catch (error) {
      this.removing = false;
      this.listError = errorMessage(this, collectErrors(error)[0]);
    } finally {
      this.busy = false;
    }
  }
}
